import * as React from "react";
import {
  AppBar,
  Box,
  ClickAwayListener,
  Drawer,
  Fade,
  FormControl,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  OutlinedInput,
  Paper,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import HistoryIcon from "@mui/icons-material/History";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";

import usersEndpoints from "../../../services/userServices";
import validator from "validator";
import ShowFilteredList from "./FilteredLists";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SidebarComponent from "../sidebar/Sidebar";

const HISTORY_KEYS = {
  youtube: "yt_search_history",
  instagram: "instagram_search_history",
};
const MAX_HISTORY = 5;

/*global chrome*/
function DashboardHeaderSection({
  isSearchOpen = true,
  updateCredits = false,
  inputRef,
  handleEnterKeyPress,
  isYoutube = false,
  hideSearch = false,
}) {
  const [cookies, removeCookie] = useCookies(["user", "auth"]);
  const [userDetails, setUserDetails] = useState({
    name: "",
    balance: "Loading...",
  });
  const [searchedCreator, setSearchedCreator] = useState("");
  const [foundCreators, setFoundCreators] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [extensionInstalled, setExtensionInstalled] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const isMobile = useMediaQuery("(max-width:600px)")
  const historyKey = isYoutube ? HISTORY_KEYS.youtube : HISTORY_KEYS.instagram;


  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem(historyKey) || "[]");
    setSearchHistory(stored);
  }, [historyKey]);

  useEffect(() => {
    async function fetchUserDetails() {
      if (cookies.user !== "undefined" && cookies.user !== undefined) {
        let response;
        try {
          response = await usersEndpoints.getUserDetails({});
        } catch (err) {
          console.error("getUserDetails failed:", err);
          setUserDetails((prev) => ({ ...prev, balance: "—" }));
          return;
        }
        checkExtensionVersion();
        if (response["success"] && response["success"] === true) {
          let user = response["user"];
          setUserDetails({
            name: user.fullName ? user.fullName : "",
            balance: response["credits"] !== undefined ? response["credits"] : 0,
            email: user.email ? user.email : "",
            image: user.image ? user.image : "",
          });
        } else if (response["success"] === false && response["logout"] === true) {
          toast.success("Your session has expired, Try logging in back again");
          removeCookie("auth", "", { path: "/", expires: new Date(1691479246) });
          removeCookie("auth", "", { path: "/filter", expires: new Date(1691479246) });
          removeCookie("user", "", { path: "/", expires: new Date(1691479246) });
          try {
            chrome.runtime.sendMessage(
              process.env.REACT_APP_EXTENSION_ID,
              { message: "logout_user" },
              function (responseFromExtension) { responseFromExtension = responseFromExtension || {}; }
            );
          } catch (error) {
            console.log(error);
          }
          window.location.href = "/";
        }
      } else {
        // A stale `auth`/`user` cookie holding the literal string "undefined" at a
        // deeper path (e.g. /payments) shadows the real ones at "/" and survives
        // every logout, which only clears "/" and "/filter". Purge those and retry
        // once instead of logging a validly signed-in user out.
        const purgedStale = ["auth", "user"].reduce((purged, name) => {
          if (!new RegExp(`(?:^|;\\s*)${name}=undefined(?:;|$)`).test(document.cookie)) return purged;
          let path = "";
          window.location.pathname.split("/").filter(Boolean).forEach((segment) => {
            path += `/${segment}`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
          });
          return true;
        }, false);
        if (purgedStale) {
          window.location.reload();
          return;
        }
        removeCookie("auth", "", { path: "/", expires: new Date(1691479246) });
        removeCookie("auth", "", { path: "/filter", expires: new Date(1691479246) });
        removeCookie("user", "", { path: "/", expires: new Date(1691479246) });
        try {
          chrome.runtime.sendMessage(
            process.env.REACT_APP_EXTENSION_ID,
            { message: "logout_user" },
            function (responseFromExtension) { responseFromExtension = responseFromExtension || {}; }
          );
        } catch (error) {
          console.log(error);
        }
        window.location.href = "/";
      }
    }
    fetchUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateCredits, removeCookie]);

  useEffect(() => {
    searchInfluencer();
  }, [searchedCreator]);

  const handleSearchInputChange = (event) => {
    setSearchedCreator(event.target.value);
  };

  const saveToHistory = (keyword) => {
    if (!keyword?.trim()) return;
    const trimmed = keyword.trim();
    const stored = JSON.parse(localStorage.getItem(historyKey) || "[]");
    const filtered = stored.filter(
      (item) => item.keyword.toLowerCase() !== trimmed.toLowerCase()
    );
    const updated = [{ keyword: trimmed, timestamp: Date.now() }, ...filtered].slice(0, MAX_HISTORY);
    localStorage.setItem(historyKey, JSON.stringify(updated));
    setSearchHistory(updated);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setHistoryOpen(true);
  };

  const handleKeyPressWithHistory = (e) => {
    if (e.key === "Enter") {
      saveToHistory(e.target.value);
      setHistoryOpen(false);
      setInputValue("");
    }
    handleEnterKeyPress?.(e);
  };

  const handleHistoryItemClick = (keyword) => {
    setInputValue(keyword);
    setHistoryOpen(false);
    saveToHistory(keyword);
    handleEnterKeyPress?.({ key: "Enter", target: { value: keyword } });
  };

  const clearHistory = () => {
    localStorage.removeItem(historyKey);
    setSearchHistory([]);
  };

  const removeHistoryItem = (keyword) => {
    const updated = searchHistory.filter(
      (item) => item.keyword.toLowerCase() !== keyword.toLowerCase()
    );
    localStorage.setItem(historyKey, JSON.stringify(updated));
    setSearchHistory(updated);
  };

  const filteredHistory = searchHistory.filter((item) =>
    item.keyword.toLowerCase().includes(inputValue.toLowerCase())
  );

  const formatRelativeTime = (ts) => {
    const diffMs = Date.now() - ts;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  };

  const searchInfluencer = async () => {
    if (searchedCreator.length > 2) {
      let isLink = validator.isURL(searchedCreator);
      let username = "";
      let type = "text";
      if (isLink) {
        try {
          let url = new URL(searchedCreator);
          if (url.host.indexOf("instagram") === -1) {
            throw new Error("Not a proper instagram url");
          }
          username = url.pathname.replaceAll("/", "");
          type = "url";
        } catch (error) {
          toast.error(error.message);
          return;
        }
      } else {
        username = searchedCreator;
      }

      const postdata = { searched: username, searchtype: type };
      const response = await usersEndpoints.searchInfluencer(postdata);
      if (response["success"] === true) {
        setFoundCreators(response["data"]);
      } else {
        toast.error(response["message"]);
      }
    } else {
      setFoundCreators([]);
    }
  };

  function checkExtensionVersion() {
    try {
      chrome.runtime.sendMessage(
        process.env.REACT_APP_EXTENSION_ID,
        { message: "version" },
        function (response) {
          if (response && response["status"] === "ok") {
            setExtensionInstalled(true);
          } else {
            setExtensionInstalled(false);
          }
        }
      );
    } catch (error) {
      setExtensionInstalled(false);
    }
  }

  const searchPlaceholder = isYoutube
    ? "Search for channel names (e.g. @channelname) and keywords from about"
    : "Search for user names and keywords from bios";

  const searchWithHistory = (
    <Box sx={{ flexGrow: 1 }} component="div">
      <ClickAwayListener onClickAway={() => setHistoryOpen(false)}>
        <Box sx={{ width: { xs: "100%", md: "50%" }, position: "relative" }}>
          <FormControl sx={{ width: "100%" }}>
            <OutlinedInput
              id="inputWithIconAdornment_"
              endAdornment={<PersonSearchIcon />}
              placeholder={searchPlaceholder}
              inputProps={{ style: { paddingTop: "9px", paddingBottom: "9px" } }}
              inputRef={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onFocus={() => setHistoryOpen(true)}
              onKeyPress={handleKeyPressWithHistory}
              autoComplete="off"
            />
          </FormControl>

          <Fade in={historyOpen && filteredHistory.length > 0} unmountOnExit>
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                width: "100%",
                zIndex: 10,
                mt: 0.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "12px",
                overflow: "hidden",
              }}
            >
              {/* Header */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                px={2}
                py={1}
                sx={{
                  bgcolor: "#f9fafb",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: "text.secondary",
                  }}
                >
                  Recent Searches
                </Typography>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.3}
                  sx={{
                    cursor: "pointer",
                    color: "#D73800",
                    opacity: 0.8,
                    transition: "opacity 0.15s",
                    "&:hover": { opacity: 1 },
                  }}
                  onClick={clearHistory}
                >
                  <DeleteOutlineIcon sx={{ fontSize: "0.85rem" }} />
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 500 }}>
                    Clear all
                  </Typography>
                </Stack>
              </Stack>

              {/* Items */}
              <Box sx={{ maxHeight: 248, overflowY: "auto" }}>
                <MenuList dense disablePadding>
                  {filteredHistory.map((item, idx) => (
                    <MenuItem
                      key={`${item.keyword}-${idx}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleHistoryItemClick(item.keyword);
                      }}
                      sx={{
                        py: 1,
                        pr: 1,
                        transition: "background-color 0.15s",
                        "&:hover": { bgcolor: "rgba(215, 56, 0, 0.04)" },
                        "&:hover .history-remove-btn": { opacity: 1 },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 34 }}>
                        <HistoryIcon
                          sx={{ fontSize: "1rem", color: "rgba(215, 56, 0, 0.4)" }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={item.keyword}
                        secondary={formatRelativeTime(item.timestamp)}
                        primaryTypographyProps={{
                          variant: "body2",
                          fontWeight: 500,
                        }}
                        secondaryTypographyProps={{ sx: { fontSize: "0.7rem" } }}
                      />
                      <Tooltip title="Remove" placement="right">
                        <IconButton
                          size="small"
                          className="history-remove-btn"
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            removeHistoryItem(item.keyword);
                          }}
                          sx={{
                            ml: 1,
                            opacity: 0,
                            transition: "opacity 0.15s ease, color 0.15s ease",
                            color: "text.disabled",
                            "&:hover": {
                              color: "#D73800",
                              bgcolor: "rgba(215, 56, 0, 0.08)",
                            },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: "0.85rem" }} />
                        </IconButton>
                      </Tooltip>
                    </MenuItem>
                  ))}
                </MenuList>
              </Box>
            </Paper>
          </Fade>
        </Box>
      </ClickAwayListener>
    </Box>
  );

  return (
    <Box
      width={{ xs: "100%", lg: `calc(100% - 80px)` }}
      marginLeft={{ xs: "0px", lg: `80px` }}
      sx={{ position: "fixed", top: 0, zIndex: 100 }}
    >
      <AppBar
        position="sticky"
        sx={{ bgcolor: "#fff", color: "dark.main" }}
      >
        <Toolbar sx={{ display: "flex",alignItems:'center',gap:1,justifyContent: "space-between",overflow:'hidden' }}>
          <Tooltip
            title="Click to show options"
            sx={{ marginRight: 2, display: { xs: "block", lg: "none" } }}
          >
            <IconButton onClick={() => setShowMenu(true)}>
              <MenuOpenIcon />
            </IconButton>
          </Tooltip>

          {hideSearch && <Box sx={{ flexGrow: 1 }} />}

          {!hideSearch && (
            isSearchOpen ? (
              <Box sx={{ flex: 1,minWidth:0,mx:1 }} component="div">
                <FormControl sx={{ width: { md: "70%",xs:'100%',sm:'100%',lg:'50%' }}}>
                  <OutlinedInput
                    id="inputWithIconAdornment"
                    endAdornment={
                      <InputAdornment position="start">
                        {foundCreators.length > 0 ? (
                          <HighlightOffIcon
                            sx={{ cursor: "pointer",fontSize:{
                              xs:18,
                              sm:22
                            }}}
                            onClick={() => setSearchedCreator("")}
                          />
                        ) : (
                          <PersonSearchIcon sx={{fontSize:{
                            xs:18,
                            sm:22
                          }}} />
                        )}
                      </InputAdornment>
                    }
                    placeholder={isMobile ? "Search influencer":"Search for an influencer"}
                    inputProps={{ style: { paddingTop: "9px", paddingBottom: "9px" } }}
                    onChange={handleSearchInputChange}
                    onDrop={handleSearchInputChange}
                    value={searchedCreator}
                    sx={{
                      width:'100%',
                      fontSize:{
                        xs:'0.8rem',
                        sm:'0.9rem',
                        md:'1rem'
                      },
                      '& .MuiOutlinedInput-input':{
                        py:{
                          xs:1,
                          sm:1.2
                        },
                        px:{
                          xs:1,
                          sm:1.5,
                        },
                        fontSize:{
                          xs:'0.8rem',
                          sm:'0.9rem',
                          md:'1rem',
                        }
                      }
                    }}
                  />
                </FormControl>
                {foundCreators.length > 0 && (
                  <ShowFilteredList users={foundCreators} installed={extensionInstalled} />
                )}
              </Box>
            ) : (
              searchWithHistory
            )
          )}

          <Stack
            direction="row"
            sx={{ flexGrow: 0, alignItems: "center" }}
            spacing={1}
            whiteSpace="nowrap"
            flexShrink={0}
          >
            <Stack>
              <Typography
                variant="p"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <MonetizationOnIcon sx={{ color: "#D73800",fontSize:{
                  xs:18,
                  sm:22
                } }} />
                <Typography variant="body1" display="block" sx={{ color: "#D73800",fontSize:{
                  xs:12,
                  sm:16
                } }}>
                  {userDetails.balance}
                </Typography>
              </Typography>
              <Typography>
                <a
                  href="/#pricing_container"
                  style={{ textDecoration: "none", color: "#D73800", fontSize: "0.7rem" }}
                >
                  Buy Coins
                </a>
              </Typography>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>

      <ToastContainer
        style={{ fontSize: "0.85rem" }}
        hideProgressBar={true}
        autoClose={2000}
        position="top-center"
      />

      <Drawer
        variant="temporary"
        anchor="left"
        open={showMenu}
        onClose={() => setShowMenu(false)}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 80 },
        }}
        ModalProps={{ keepMounted: true }}
      >
        <SidebarComponent />
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": { boxSizing: "border-box", width: 80 },
        }}
        open
      >
        <SidebarComponent />
      </Drawer>
    </Box>
  );
}

export default DashboardHeaderSection;
